/**
 * Expense Report (BE-14d-2)
 *
 * Expenses dated in the range: totals, a grouping by category, vendor or payment method (tabs), then every expense.
 * A return is negative; "Not paid yet" is a pay-later expense still unpaid. A row opens the expense.
 * Route: /reports/expenses
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, TextField, MenuItem, Tabs, Tab, Box } from '@mui/material';
import ReportPage, { ReportTable, formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { expenseService } from '../../services/accounting/expense.service';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const TYPES = [
  { value: '', label: 'All' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PAYABLE', label: 'Pay later' },
  { value: 'RETURN', label: 'Return' },
];
const METHODS = {
  BANK: 'Bank', CASH: 'Cash', CHEQUE: 'Cheque', CREDIT_CARD: 'Credit card', E_WALLET: 'E-wallet', UNPAID: 'Not paid yet',
};
const GROUPS = [
  { key: 'byCategory', label: 'By category', name: 'Category' },
  { key: 'byVendor', label: 'By vendor', name: 'Vendor' },
  { key: 'byMethod', label: 'By method', name: 'Method' },
];
const typeLabel = (t) => TYPES.find((x) => x.value === t)?.label || t || '-';
const methodLabel = (m) => METHODS[m] || m || '-';

const ExpenseReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [to, setTo] = useState(toYmd(new Date()));
  const [categoryId, setCategoryId] = useState('');
  const [expenseType, setExpenseType] = useState('');
  const [categories, setCategories] = useState([]);
  const [group, setGroup] = useState(0);

  useEffect(() => {
    expenseService.getCategories().then((c) => setCategories(c || [])).catch(() => {});
  }, []);

  const params = { from, to, categoryId, expenseType };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getExpenseReport({ from, to, categoryId, expenseType }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the expense report'));
    } finally {
      setLoading(false);
    }
  }, [from, to, categoryId, expenseType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const g = GROUPS[group];
  const groupColumns = [
    { key: 'name', label: g.name, render: (r) => (g.key === 'byMethod' ? methodLabel(r.name) : r.name || '-') },
    ...(g.key === 'byCategory' ? [{ key: 'group', label: 'Group', render: (r) => r.group || '-' }] : []),
    { key: 'count', label: 'Expenses', align: 'right', total: (x) => x.count },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatCurrency(r.amount), total: (x) => formatCurrency(x.amountTotal) },
    { key: 'vat', label: 'VAT', align: 'right', render: (r) => formatCurrency(r.vat), total: (x) => formatCurrency(x.vatTotal) },
    { key: 'total', label: 'Total', align: 'right', render: (r) => formatCurrency(r.total), total: (x) => formatCurrency(x.total) },
  ];

  const columns = [
    { key: 'expenseNumber', label: 'Expense' },
    { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
    { key: 'category', label: 'Category', render: (r) => r.category || '-' },
    { key: 'vendor', label: 'Vendor', render: (r) => r.vendor || '-' },
    { key: 'description', label: 'Description', render: (r) => r.description || '-' },
    { key: 'expenseType', label: 'Type', render: (r) => typeLabel(r.expenseType) },
    { key: 'method', label: 'Method', render: (r) => methodLabel(r.method) },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatCurrency(r.amount), total: (x) => formatCurrency(x.amountTotal) },
    { key: 'vat', label: 'VAT', align: 'right', render: (r) => formatCurrency(r.vat), total: (x) => formatCurrency(x.vatTotal) },
    { key: 'total', label: 'Total', align: 'right', render: (r) => formatCurrency(r.total), total: (x) => formatCurrency(x.total) },
  ];

  const cards = report ? [
    { label: 'Expenses', value: report.count },
    { label: 'Amount', value: formatCurrency(report.amountTotal) },
    { label: 'VAT', value: formatCurrency(report.vatTotal) },
    { label: 'Total', value: formatCurrency(report.total), tone: 'bad' },
  ] : [];

  return (
    <ReportPage
      title="Expense Report"
      subtitle={`Expenses dated ${formatDate(from)} – ${formatDate(to)} by category, vendor and payment method`}
      exportType="expenses" exportParams={params} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.rows || []} rowKey={(r) => r.expenseId}
      totals={report} totalsLabel={`TOTAL (${report?.count || 0} expenses)`}
      onRowClick={(r) => navigate(`/accounting/expenses/${r.expenseId}`)}
      emptyMessage="No expense in this range."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="From" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="To" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <MenuItem value="">All categories</MenuItem>
              {categories.filter((c) => c.active !== false).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField select fullWidth size="small" label="Type" value={expenseType} onChange={(e) => setExpenseType(e.target.value)}>
              {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      )}
    >
      {!loading && report && (
        <Box>
          <Tabs value={group} onChange={(e, v) => setGroup(v)} sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.8125rem' } }}>
            {GROUPS.map((x) => <Tab key={x.key} label={x.label} />)}
          </Tabs>
          <ReportTable
            key={g.key}
            columns={groupColumns} rows={report[g.key] || []} rowKey={(r, i) => `${g.key}-${r.name}-${i}`}
            totals={report} totalsLabel="TOTAL" emptyMessage="No expense in this range."
          />
        </Box>
      )}
    </ReportPage>
  );
};

export default ExpenseReport;
